import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Checkbox,
  Divider,
  Button,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import Cookies from 'js-cookie';

/**
 * Component for adding participants to an existing group chat
 * @param {Object} group - The group chat to add participants to
 * @param {Function} onClose - Function to call when closing the component
 * @param {Function} onUsersAdded - Function to call when users are added
 */
const AddGroupParticipants = ({ group, onClose, onUsersAdded }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users when search query changes
  useEffect(() => {
    filterUsers();
  }, [searchQuery, users]);

  // Fetch users from the API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('token');
      const response = await axios.get(
        `${process.env.REACT_APP_STATUS_API_URL}/admin/list_users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.users) {
        // Filter out users already in the group
        const usersNotInGroup = response.data.users.filter(user => 
          !group.participants?.includes(user.username)
        );
        setUsers(usersNotInGroup);
        filterUsers(usersNotInGroup, searchQuery);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search query
  const filterUsers = (userList = users, query = searchQuery) => {
    if (!query) {
      setFilteredUsers(userList);
      return;
    }
    
    const lowercaseQuery = query.toLowerCase();
    const filtered = userList.filter(user => 
      user.username.toLowerCase().includes(lowercaseQuery) ||
      (user.fornavn && user.fornavn.toLowerCase().includes(lowercaseQuery)) ||
      (user.etternavn && user.etternavn.toLowerCase().includes(lowercaseQuery))
    );
    
    setFilteredUsers(filtered);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle user selection
  const handleToggleUser = (username) => {
    setSelectedUsers(prev => 
      prev.includes(username) 
        ? prev.filter(u => u !== username) 
        : [...prev, username]
    );
  };

  // Handle adding selected users to group
  const handleAddUsers = async () => {
    if (selectedUsers.length === 0) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      const token = Cookies.get('token');
      const response = await axios.post(
        `${process.env.REACT_APP_STATUS_API_URL}/chat/add_participants`,
        {
          group_id: group.id,
          participants: selectedUsers
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.added_users) {
        // Call the callback with the added users
        if (onUsersAdded) {
          onUsersAdded(response.data.added_users);
        }
        
        // Close the component
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Failed to add users:', error);
      setError('Failed to add users to the group. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      p: 2 
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2 
      }}>
        <Typography variant="h6" sx={{ color: '#e0e0e0' }}>
          Add Participants
        </Typography>
        <IconButton 
          onClick={onClose}
          size="small"
          sx={{ 
            color: '#9e9e9e',
            '&:hover': { color: '#ff5252' } 
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search input */}
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search users..."
        value={searchQuery}
        onChange={handleSearchChange}
        sx={{ 
          mb: 2,
          '.MuiOutlinedInput-root': {
            bgcolor: '#363636',
            color: '#e0e0e0',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4a9bfc'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4a9bfc'
            }
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#9e9e9e' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* User list */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        bgcolor: '#252526',
        borderRadius: 1,
        mb: 2
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: '100%' 
          }}>
            <CircularProgress size={24} sx={{ color: '#4a9bfc' }} />
          </Box>
        ) : filteredUsers.length > 0 ? (
          <List disablePadding>
            {filteredUsers.map(user => (
              <React.Fragment key={user.username}>
                <ListItem
                  dense
                  button
                  onClick={() => handleToggleUser(user.username)}
                  sx={{
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' }
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedUsers.includes(user.username)}
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      color: '#9e9e9e',
                      '&.Mui-checked': { color: '#4a9bfc' }
                    }}
                  />
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#4a9bfc' }}>
                      {user.fornavn?.[0]}{user.etternavn?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${user.fornavn || ''} ${user.etternavn || ''}`}
                    secondary={user.username}
                    primaryTypographyProps={{ color: '#e0e0e0', noWrap: true }}
                    secondaryTypographyProps={{ color: '#9e9e9e', noWrap: true }}
                  />
                </ListItem>
                <Divider component="li" sx={{ bgcolor: 'rgba(255, 255, 255, 0.08)' }} />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: '100%',
            color: '#9e9e9e' 
          }}>
            <Typography>No users found</Typography>
          </Box>
        )}
      </Box>

      {/* Error message */}
      {error && (
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#ff5252', 
            mb: 2, 
            textAlign: 'center' 
          }}
        >
          {error}
        </Typography>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            color: '#9e9e9e',
            borderColor: '#9e9e9e',
            '&:hover': {
              borderColor: '#e0e0e0',
              color: '#e0e0e0'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAddUsers}
          disabled={selectedUsers.length === 0 || submitting}
          sx={{
            bgcolor: '#4a9bfc',
            '&:hover': {
              bgcolor: '#3a8bec'
            },
            '&.Mui-disabled': {
              bgcolor: '#252526'
            }
          }}
        >
          {submitting ? (
            <CircularProgress size={24} sx={{ color: '#fff' }} />
          ) : (
            `Add ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default AddGroupParticipants;
